// @ts-check
// Configurações > API e webhooks (doc 14, seções 5 e 8; contrato 08): chaves de API com o token mostrado uma vez,
// webhooks de saída por objeto e ação, com segredo, teste e log de entregas.
import { html } from 'htm/preact';
import { useEffect, useState } from 'preact/hooks';
import { PageCard, PageHeader } from '../page/page-card.js';
import { SettingsSection } from './settings-pages.js';
import { CopyableValue } from './settings-channels-page.js';
import { AdminOnlyNote, CodeSnippet, FormError, SelectField, StatusBadge, TextField, describeError } from './integration-ui.js';
import {
  createApiKey, createWebhook, deleteWebhook, getWebhook, listApiKeys, listWebhookDeliveries, listWebhooks, regenerateApiKey, revokeApiKey, testWebhook,
  updateWebhook,
} from '../../data/integrations.js';
import { hasCurrentPermissionFlag, objectsState } from '../../data/metadata-store.js';
import { Link, navigate } from '../../app/router.js';
import { formatDateTime, formatRelativeToNow } from '../../lib/format.js';
import { Button, LightIconButton } from '../../ui/button.js';
import { Skeleton, Toggle } from '../../ui/feedback.js';
import { Icon } from '../../ui/icon.js';
import { Modal } from '../../ui/modal.js';
import { showErrorSnackbar, showSuccessSnackbar } from '../../ui/snackbar.js';
import { t, translateMetadataLabel } from '../../i18n/index.js';
import { joinWebhookOperation, splitWebhookOperation } from '../../lib/webhook-operations.js';

/**
 * @typedef {import('../../data/integrations.js').ApiKey} ApiKey
 * @typedef {import('../../data/integrations.js').Webhook} Webhook
 * @typedef {import('../../data/integrations.js').WebhookDelivery} WebhookDelivery
 */

const EXPIRATION_OPTIONS = ['never', '15', '30', '90', '365', '730', 'custom'];
const WEBHOOK_ACTIONS = ['*', 'created', 'updated', 'deleted', 'destroyed', 'restored'];
// eventos de metadados (como no Twenty): objeto e campo criados, alterados e excluídos
const METADATA_OBJECT_OPTIONS = ['metadata.*', 'metadata.objectMetadata', 'metadata.fieldMetadata'];
const METADATA_ACTIONS = ['*', 'created', 'updated', 'deleted'];

/** @param {string} objectName */
const isMetadataOperation = (objectName) => objectName.startsWith('metadata.');

/** @param {string} objectName "metadata.*", "metadata.objectMetadata"... */
const metadataOptionLabel = (objectName) => t(`publicApi.webhooks.metadataObjects.${objectName === 'metadata.*' ? 'all' : objectName.replace('metadata.', '')}`);

// chaves de API, webhooks e Lead Ads seguem a flag API_KEYS_AND_WEBHOOKS do papel
const isAdmin = () => hasCurrentPermissionFlag('API_KEYS_AND_WEBHOOKS');

/** @param {ApiKey} apiKey */
function apiKeyStatus(apiKey) {
  if (apiKey.revokedAt) return 'REVOKED';
  if (apiKey.expiresAt && Date.parse(apiKey.expiresAt) <= Date.now()) return 'EXPIRED';
  return 'ACTIVE';
}

/**
 * @param {{ onCreated: (result: { apiKey: ApiKey, token: string }) => void, onClose: () => void }} props
 */
function CreateApiKeyModal({ onCreated, onClose }) {
  const [name, setName] = useState('');
  const [role, setRole] = useState(/** @type {'ADMIN' | 'MEMBER'} */ ('MEMBER'));
  const [expiration, setExpiration] = useState('never');
  const [expirationDate, setExpirationDate] = useState('');
  const [error, setError] = useState(/** @type {string | null} */ (null));
  const [isSaving, setIsSaving] = useState(false);
  const submit = async () => {
    setIsSaving(true);
    setError(null);
    try {
      const expirationInput = expiration === 'custom'
        ? { expiresAt: expirationDate }
        : { expiresInDays: expiration === 'never' ? null : Number(expiration) };
      onCreated(await createApiKey({ name: name.trim(), role, ...expirationInput }));
    } catch (requestError) {
      setError(describeError(requestError));
    } finally {
      setIsSaving(false);
    }
  };
  return html`<${Modal} title=${t('leadCapture.apiKeys.newTitle')} subtitle=${t('leadCapture.apiKeys.newSubtitle')} confirmLabel=${t('leadCapture.apiKeys.create')}
    isConfirmDisabled=${!name.trim() || (expiration === 'custom' && !expirationDate)} isLoading=${isSaving} onClose=${onClose} onConfirm=${submit}>
    <div class="integration-modal-body">
      <${TextField} label=${t('leadCapture.apiKeys.name')} value=${name} onInput=${setName} placeholder=${t('leadCapture.apiKeys.namePlaceholder')} isRequired=${true} maxLength=${80} autoFocus=${true} />
      <${SelectField} label=${t('leadCapture.apiKeys.role')} value=${role} onChange=${(value) => setRole(/** @type {'ADMIN' | 'MEMBER'} */ (value))}
        hint=${t('leadCapture.apiKeys.roleHint')}
        options=${[{ value: 'MEMBER', label: t('leadCapture.apiKeys.roles.MEMBER') }, { value: 'ADMIN', label: t('leadCapture.apiKeys.roles.ADMIN') }]} />
      <${SelectField} label=${t('leadCapture.apiKeys.expiration')} value=${expiration} onChange=${setExpiration}
        options=${EXPIRATION_OPTIONS.map((option) => ({ value: option, label: option === 'custom' ? t('publicApi.apiKeys.customDate') : t(`leadCapture.apiKeys.expirationOptions.${option}`) }))} />
      ${expiration === 'custom'
        ? html`<${TextField} label=${t('publicApi.apiKeys.expirationDate')} type="date" value=${expirationDate} onInput=${setExpirationDate}
            hint=${t('publicApi.apiKeys.expirationDateHint')} isRequired=${true} />`
        : null}
      <${FormError} message=${error} />
    </div>
  <//>`;
}

/**
 * @param {{ token: string, onClose: () => void }} props
 */
function NewTokenModal({ token, onClose }) {
  return html`<${Modal} title=${t('leadCapture.apiKeys.tokenTitle')} subtitle=${t('leadCapture.apiKeys.tokenSubtitle')} confirmLabel=${t('leadCapture.common.done')}
    onClose=${onClose} onConfirm=${onClose} hideCancel=${true}>
    <div class="integration-modal-body">
      <${CopyableValue} value=${token} label=${t('leadCapture.common.copy')} />
      <p class="integration-warning"><${Icon} name="IconAlertTriangle" size=${14} />${t('leadCapture.apiKeys.tokenWarning')}</p>
    </div>
  <//>`;
}

function ApiKeysSection() {
  const [apiKeys, setApiKeys] = useState(/** @type {ApiKey[] | null} */ (null));
  const [isCreating, setIsCreating] = useState(false);
  const [newToken, setNewToken] = useState(/** @type {string | null} */ (null));
  const [revoking, setRevoking] = useState(/** @type {ApiKey | null} */ (null));
  const [regenerating, setRegenerating] = useState(/** @type {ApiKey | null} */ (null));
  const load = () => listApiKeys().then(setApiKeys).catch((error) => {
    showErrorSnackbar(error);
    setApiKeys([]);
  });
  useEffect(() => {
    load();
  }, []);
  const origin = window.location.origin;
  return html`<${SettingsSection} title=${t('leadCapture.apiKeys.title')} description=${t('leadCapture.apiKeys.description')}>
    <div class="integration-toolbar">
      <${Button} title=${t('apiReference.open')} icon="IconBook" variant="secondary" size="small" onClick=${() => navigate('/settings/developers/api-reference')} />
      <${Button} title=${t('leadCapture.apiKeys.new')} icon="IconPlus" accent="blue" size="small" onClick=${() => setIsCreating(true)} />
    </div>
    ${apiKeys === null
      ? html`<${Skeleton} height=${80} radius=${8} />`
      : apiKeys.length === 0
        ? html`<div class="integration-empty">${t('leadCapture.apiKeys.empty')}</div>`
        : html`<div class="settings-table integration-table">
            <div class="settings-table-row settings-table-header" data-columns="api-keys">
              <span>${t('leadCapture.apiKeys.name')}</span><span>${t('leadCapture.apiKeys.key')}</span><span>${t('leadCapture.apiKeys.role')}</span>
              <span>${t('leadCapture.apiKeys.lastUsed')}</span><span>${t('leadCapture.common.status')}</span><span></span>
            </div>
            ${apiKeys.map((apiKey) => {
              const status = apiKeyStatus(apiKey);
              return html`<div key=${apiKey.id} class="settings-table-row" data-columns="api-keys">
                <span class="settings-table-name"><${Icon} name="IconKey" size=${16} />${apiKey.name}</span>
                <span class="settings-table-muted settings-table-mono">${apiKey.tokenPrefix}...</span>
                <span class="settings-table-muted">${t(`leadCapture.apiKeys.roles.${apiKey.role}`)}</span>
                <span class="settings-table-muted" title=${formatDateTime(apiKey.lastUsedAt)}>${apiKey.lastUsedAt ? formatRelativeToNow(apiKey.lastUsedAt) : t('leadCapture.apiKeys.neverUsed')}</span>
                <span>
                  <${StatusBadge} status=${status} label=${t(`leadCapture.apiKeys.statuses.${status}`)} />
                  ${apiKey.expiresAt && status === 'ACTIVE' ? html`<span class="integration-muted-small">${t('leadCapture.apiKeys.expiresOn', { date: formatDateTime(apiKey.expiresAt) })}</span>` : null}
                </span>
                <span class="api-key-actions">${status === 'ACTIVE'
                  ? html`<${LightIconButton} icon="IconRefresh" ariaLabel=${t('publicApi.apiKeys.regenerate')} onClick=${() => setRegenerating(apiKey)} />
                    <${LightIconButton} icon="IconTrash" ariaLabel=${t('leadCapture.apiKeys.revoke')} onClick=${() => setRevoking(apiKey)} />`
                  : null}</span>
              </div>`;
            })}
          </div>`}
    <${CodeSnippet} label=${t('leadCapture.apiKeys.exampleLabel')}
      code=${`curl ${origin}/rest/people?limit=5 \\\n  -H "Authorization: Bearer mm_..."\n\ncurl -X POST ${origin}/rest/leads \\\n  -H "Authorization: Bearer mm_..." -H "Content-Type: application/json" \\\n  -d '{"guardian":{"name":"Ana Souza","phone":"11 98765-4321"},"students":[{"name":"Pedro"}],"leadSource":"WEBSITE"}'`} />
    ${isCreating ? html`<${CreateApiKeyModal} onClose=${() => setIsCreating(false)} onCreated=${(/** @type {any} */ result) => {
      setIsCreating(false);
      setNewToken(result.token);
      load();
    }} />` : null}
    ${newToken ? html`<${NewTokenModal} token=${newToken} onClose=${() => setNewToken(null)} />` : null}
    ${regenerating ? html`<${Modal} title=${t('publicApi.apiKeys.regenerateTitle')} subtitle=${t('publicApi.apiKeys.regenerateSubtitle', { name: regenerating.name })}
      confirmLabel=${t('publicApi.apiKeys.regenerate')} confirmAccent="danger" onClose=${() => setRegenerating(null)} onConfirm=${async () => {
        try {
          const result = await regenerateApiKey(regenerating.id);
          setNewToken(result.token);
          load();
        } catch (error) {
          showErrorSnackbar(error);
        }
        setRegenerating(null);
      }} />` : null}
    ${revoking ? html`<${Modal} title=${t('leadCapture.apiKeys.revokeTitle')} subtitle=${t('leadCapture.apiKeys.revokeSubtitle', { name: revoking.name })}
      confirmLabel=${t('leadCapture.apiKeys.revoke')} confirmAccent="danger" onClose=${() => setRevoking(null)} onConfirm=${async () => {
        try {
          await revokeApiKey(revoking.id);
          showSuccessSnackbar(t('leadCapture.apiKeys.revokedSnackbar'));
          load();
        } catch (error) {
          showErrorSnackbar(error);
        }
        setRevoking(null);
      }} />` : null}
  <//>`;
}

/** @param {string[]} operations */
function describeOperations(operations) {
  if (operations.includes('*.*')) return t('leadCapture.webhooks.allEvents');
  return operations.map((operation) => {
    const { objectName, action } = splitWebhookOperation(operation);
    if (isMetadataOperation(objectName)) return `${metadataOptionLabel(objectName)}: ${t(`leadCapture.webhooks.actions.${action === '*' ? 'all' : action}`)}`;
    const objectMetadata = objectsState.value.find((candidate) => candidate.nameSingular === objectName);
    const objectLabel = objectName === '*' ? t('leadCapture.webhooks.allObjects') : translateMetadataLabel(objectMetadata?.labelPlural ?? objectName);
    return `${objectLabel}: ${t(`leadCapture.webhooks.actions.${action === '*' ? 'all' : action}`)}`;
  }).join(', ');
}

function WebhooksSection() {
  const [webhooks, setWebhooks] = useState(/** @type {Webhook[] | null} */ (null));
  useEffect(() => {
    listWebhooks().then(setWebhooks).catch((error) => {
      showErrorSnackbar(error);
      setWebhooks([]);
    });
  }, []);
  return html`<${SettingsSection} title=${t('leadCapture.webhooks.title')} description=${t('leadCapture.webhooks.description')}>
    <div class="integration-toolbar">
      <${Button} title=${t('leadCapture.webhooks.new')} icon="IconPlus" accent="blue" size="small" onClick=${() => navigate('/settings/developers/webhooks/new')} />
    </div>
    ${webhooks === null
      ? html`<${Skeleton} height=${80} radius=${8} />`
      : webhooks.length === 0
        ? html`<div class="integration-empty">${t('leadCapture.webhooks.empty')}</div>`
        : html`<div class="settings-table integration-table">
            <div class="settings-table-row settings-table-header" data-columns="webhooks">
              <span>${t('leadCapture.webhooks.url')}</span><span>${t('leadCapture.webhooks.events')}</span><span>${t('leadCapture.common.status')}</span><span></span>
            </div>
            ${webhooks.map((webhook) => html`<${Link} key=${webhook.id} href=${`/settings/developers/webhooks/${webhook.id}`} className="settings-table-row" data-columns="webhooks">
              <span class="settings-table-name"><${Icon} name="IconWebhook" size=${16} /><span class="integration-ellipsis">${webhook.targetUrl}</span></span>
              <span class="settings-table-muted integration-ellipsis">${describeOperations(webhook.operations)}</span>
              <span><${StatusBadge} status=${webhook.isActive ? 'ACTIVE' : 'PAUSED'} label=${webhook.isActive ? t('leadCapture.webhooks.active') : t('leadCapture.webhooks.paused')} /></span>
              <span class="settings-table-chevron"><${Icon} name="IconChevronRight" size=${16} /></span>
            <//>`)}
          </div>`}
  <//>`;
}

export function SettingsDevelopersPage() {
  useEffect(() => {
    document.title = `${t('leadCapture.developers.title')} - ${t('navigation.settings')}`;
  }, []);
  return html`<${PageCard} header=${html`<${PageHeader} icon="IconApi" title=${t('leadCapture.developers.title')} />`}>
    <div class="settings-page">
      ${isAdmin()
        ? html`<${ApiKeysSection} /><${WebhooksSection} />`
        : html`<${SettingsSection} title=${t('leadCapture.developers.title')}><${AdminOnlyNote} /><//>`}
    </div>
  <//>`;
}

/**
 * @param {{ operations: Array<{ objectName: string, action: string }>, onChange: (operations: Array<{ objectName: string, action: string }>) => void }} props
 */
function OperationsEditor({ operations, onChange }) {
  const objectOptions = [
    { value: '*', label: t('leadCapture.webhooks.allObjects') },
    ...objectsState.value
      .filter((objectMetadata) => !objectMetadata.isSystem)
      .map((objectMetadata) => ({ value: objectMetadata.nameSingular, label: translateMetadataLabel(objectMetadata.labelPlural) }))
      .sort((first, second) => first.label.localeCompare(second.label)),
  ];
  // eventos de metadados (objeto e campo), depois dos objetos
  for (const option of METADATA_OBJECT_OPTIONS) objectOptions.push({ value: option, label: metadataOptionLabel(option) });
  // operação de um objeto que a tela não conhece (criado pela API, ou removido depois) continua visível
  for (const operation of operations) {
    if (!objectOptions.some((option) => option.value === operation.objectName)) objectOptions.push({ value: operation.objectName, label: operation.objectName });
  }
  /** @param {string} objectName */
  const actionOptionsFor = (objectName) => (isMetadataOperation(objectName) ? METADATA_ACTIONS : WEBHOOK_ACTIONS)
    .map((action) => ({ value: action, label: t(`leadCapture.webhooks.actions.${action === '*' ? 'all' : action}`) }));
  return html`<div class="integration-operations">
    ${operations.map((operation, index) => {
      const actionOptions = actionOptionsFor(operation.objectName);
      return html`<div key=${index} class="integration-operation-row">
      <select class="text-input integration-select" aria-label=${t('leadCapture.webhooks.object')} value=${operation.objectName}
        onChange=${(/** @type {Event} */ event) => onChange(operations.map((item, itemIndex) => {
          if (itemIndex !== index) return item;
          const objectName = /** @type {HTMLSelectElement} */ (event.currentTarget).value;
          // destruir e restaurar não existem para metadados
          const action = isMetadataOperation(objectName) && !METADATA_ACTIONS.includes(item.action) ? '*' : item.action;
          return { objectName, action };
        }))}>
        ${objectOptions.map((option) => html`<option key=${option.value} value=${option.value} selected=${option.value === operation.objectName}>${option.label}</option>`)}
      </select>
      <select class="text-input integration-select" aria-label=${t('leadCapture.webhooks.action')} value=${operation.action}
        onChange=${(/** @type {Event} */ event) => onChange(operations.map((item, itemIndex) => (itemIndex === index ? { ...item, action: /** @type {HTMLSelectElement} */ (event.currentTarget).value } : item)))}>
        ${actionOptions.map((option) => html`<option key=${option.value} value=${option.value} selected=${option.value === operation.action}>${option.label}</option>`)}
      </select>
      <${LightIconButton} icon="IconTrash" ariaLabel=${t('leadCapture.common.remove')} disabled=${operations.length === 1}
        onClick=${() => onChange(operations.filter((_, itemIndex) => itemIndex !== index))} />
    </div>`;
    })}
    <div><${Button} title=${t('leadCapture.webhooks.addEvent')} icon="IconPlus" variant="secondary" size="small" onClick=${() => onChange([...operations, { objectName: 'person', action: 'created' }])} /></div>
  </div>`;
}

/**
 * @param {{ deliveries: WebhookDelivery[] | null, onRefresh: () => void }} props
 */
function DeliveryLog({ deliveries, onRefresh }) {
  return html`<${SettingsSection} title=${t('leadCapture.webhooks.logTitle')} description=${t('leadCapture.webhooks.logDescription')}>
    <div class="integration-toolbar"><${Button} title=${t('leadCapture.common.refresh')} icon="IconRefresh" variant="secondary" size="small" onClick=${onRefresh} /></div>
    ${deliveries === null
      ? html`<${Skeleton} height=${60} radius=${8} />`
      : deliveries.length === 0
        ? html`<div class="integration-empty">${t('leadCapture.webhooks.logEmpty')}</div>`
        : html`<div class="settings-table integration-table">
            <div class="settings-table-row settings-table-header" data-columns="deliveries">
              <span>${t('leadCapture.webhooks.when')}</span><span>${t('leadCapture.webhooks.event')}</span><span>${t('leadCapture.common.status')}</span>
              <span>${t('leadCapture.webhooks.response')}</span><span>${t('leadCapture.webhooks.attempts')}</span>
            </div>
            ${deliveries.map((delivery) => html`<div key=${delivery.id} class="settings-table-row" data-columns="deliveries" title=${delivery.lastError ?? ''}>
              <span class="settings-table-muted">${formatDateTime(delivery.createdAt)}</span>
              <span class="settings-table-mono integration-ellipsis">${delivery.eventName}</span>
              <span><${StatusBadge} status=${delivery.status} label=${t(`leadCapture.webhooks.deliveryStatuses.${delivery.status}`)} /></span>
              <span class="settings-table-muted integration-ellipsis">${delivery.lastStatusCode ? `HTTP ${delivery.lastStatusCode}` : delivery.lastError ?? '-'}</span>
              <span class="settings-table-muted">${delivery.attemptCount}</span>
            </div>`)}
          </div>`}
  <//>`;
}

/**
 * @param {{ webhookId: string }} props
 */
export function SettingsWebhookDetailPage({ webhookId }) {
  const isNew = webhookId === 'new';
  const [webhook, setWebhook] = useState(/** @type {Webhook | null} */ (null));
  const [targetUrl, setTargetUrl] = useState('');
  const [description, setDescription] = useState('');
  const [operations, setOperations] = useState([{ objectName: 'person', action: 'created' }]);
  const [isActive, setIsActive] = useState(true);
  const [deliveries, setDeliveries] = useState(/** @type {WebhookDelivery[] | null} */ (null));
  const [error, setError] = useState(/** @type {string | null} */ (null));
  const [isSaving, setIsSaving] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  /** @param {Webhook} loaded */
  const applyWebhook = (loaded) => {
    setWebhook(loaded);
    setTargetUrl(loaded.targetUrl);
    setDescription(loaded.description ?? '');
    setOperations(loaded.operations.map(splitWebhookOperation));
    setIsActive(loaded.isActive);
  };
  const loadDeliveries = () => {
    if (isNew) return;
    listWebhookDeliveries(webhookId).then(setDeliveries).catch(() => setDeliveries([]));
  };
  useEffect(() => {
    document.title = `${t('leadCapture.webhooks.title')} - ${t('navigation.settings')}`;
    if (isNew) return;
    getWebhook(webhookId).then(applyWebhook).catch((requestError) => {
      showErrorSnackbar(requestError);
      navigate('/settings/developers', { replace: true });
    });
    loadDeliveries();
  }, [webhookId]);

  const operationValues = [...new Set(operations.map(joinWebhookOperation))];
  const save = async () => {
    setIsSaving(true);
    setError(null);
    try {
      const input = { targetUrl: targetUrl.trim(), description: description.trim(), operations: operationValues, isActive };
      if (isNew) {
        const created = await createWebhook(input);
        showSuccessSnackbar(t('leadCapture.webhooks.createdSnackbar'));
        navigate(`/settings/developers/webhooks/${created.id}`, { replace: true });
      } else {
        applyWebhook(await updateWebhook(webhookId, input));
        showSuccessSnackbar(t('leadCapture.common.saved'));
      }
    } catch (requestError) {
      setError(describeError(requestError));
    } finally {
      setIsSaving(false);
    }
  };
  const runTest = async () => {
    setIsTesting(true);
    try {
      const result = await testWebhook(webhookId);
      if (result.success) showSuccessSnackbar(t('leadCapture.webhooks.testSucceeded', { status: result.statusCode ?? '' }));
      else setError(t('leadCapture.webhooks.testFailed', { reason: result.statusCode ? `HTTP ${result.statusCode}` : result.error ?? '' }));
      loadDeliveries();
    } catch (requestError) {
      showErrorSnackbar(requestError);
    } finally {
      setIsTesting(false);
    }
  };

  const breadcrumb = html`<nav class="record-breadcrumb">
    <${Link} href="/settings/developers" className="record-breadcrumb-prefix">${t('leadCapture.developers.title')}<//>
    <span class="record-breadcrumb-separator">/</span>
    <span class="record-breadcrumb-current">${isNew ? t('leadCapture.webhooks.new') : t('leadCapture.webhooks.detailTitle')}</span>
  </nav>`;

  if (!isAdmin()) return html`<${PageCard} header=${html`<${PageHeader} breadcrumb=${breadcrumb} />`}><div class="settings-page"><${AdminOnlyNote} /></div><//>`;
  if (!isNew && !webhook) return html`<${PageCard} header=${html`<${PageHeader} breadcrumb=${breadcrumb} />`}><div class="settings-page"><${Skeleton} height=${200} radius=${8} /></div><//>`;

  return html`<${PageCard} header=${html`<${PageHeader} breadcrumb=${breadcrumb} />`}>
    <div class="settings-page">
      <${SettingsSection} title=${t('leadCapture.webhooks.endpointTitle')} description=${t('leadCapture.webhooks.endpointDescription')}>
        <div class="channel-form">
          <${TextField} label=${t('leadCapture.webhooks.url')} value=${targetUrl} onInput=${setTargetUrl} placeholder="https://exemplo.com.br/webhooks/crm" isRequired=${true} type="url" />
          <${TextField} label=${t('leadCapture.webhooks.descriptionLabel')} value=${description} onInput=${setDescription} placeholder=${t('leadCapture.webhooks.descriptionPlaceholder')} maxLength=${200} />
          <div class="form-field">
            <span class="form-field-label">${t('leadCapture.webhooks.events')}</span>
            <${OperationsEditor} operations=${operations} onChange=${setOperations} />
          </div>
          <label class="integration-toggle-row">
            <${Toggle} value=${isActive} onChange=${setIsActive} ariaLabel=${t('leadCapture.webhooks.active')} />
            <span>${t('leadCapture.webhooks.activeLabel')}</span>
          </label>
          <${FormError} message=${error} />
          <div class="channel-form-actions">
            ${!isNew ? html`<${Button} title=${t('leadCapture.webhooks.test')} icon="IconPlayerPlay" variant="secondary" size="small" isLoading=${isTesting} onClick=${runTest} />` : null}
            <${Button} title=${isNew ? t('leadCapture.webhooks.create') : t('leadCapture.common.save')} icon="IconCheck" accent="blue" size="small"
              isLoading=${isSaving} disabled=${!targetUrl.trim() || operationValues.length === 0} onClick=${save} />
          </div>
        </div>
      <//>
      ${webhook?.secret
        ? html`<${SettingsSection} title=${t('leadCapture.webhooks.secretTitle')} description=${t('leadCapture.webhooks.secretDescription')}>
            <${CopyableValue} value=${webhook.secret} label=${t('leadCapture.common.copy')} />
            <${CodeSnippet} label=${t('leadCapture.webhooks.verifyLabel')}
              code=${"// Node.js: confira a assinatura antes de usar o corpo\nconst expected = crypto.createHmac('sha256', SEGREDO)\n  .update(`${req.headers['x-appcrm-webhook-timestamp']}:${corpoBruto}`)\n  .digest('hex');\nconst isValid = expected === req.headers['x-appcrm-webhook-signature'];"} />
            <div class="channel-card-actions">
              <${Button} title=${t('leadCapture.webhooks.regenerateSecret')} icon="IconRefresh" variant="secondary" size="small" onClick=${async () => {
                try {
                  applyWebhook(await updateWebhook(webhookId, { regenerateSecret: true }));
                  showSuccessSnackbar(t('leadCapture.webhooks.secretRegenerated'));
                } catch (requestError) {
                  showErrorSnackbar(requestError);
                }
              }} />
            </div>
          <//>`
        : null}
      ${!isNew ? html`<${DeliveryLog} deliveries=${deliveries} onRefresh=${loadDeliveries} />` : null}
      ${!isNew
        ? html`<${SettingsSection} title=${t('leadCapture.common.dangerZone')}>
            <div><${Button} title=${t('leadCapture.webhooks.delete')} icon="IconTrash" variant="secondary" accent="danger" size="small" onClick=${() => setIsDeleting(true)} /></div>
          <//>`
        : null}
      ${isDeleting ? html`<${Modal} title=${t('leadCapture.webhooks.deleteTitle')} subtitle=${t('leadCapture.webhooks.deleteSubtitle')} confirmLabel=${t('leadCapture.webhooks.delete')}
        confirmAccent="danger" onClose=${() => setIsDeleting(false)} onConfirm=${async () => {
          try {
            await deleteWebhook(webhookId);
            showSuccessSnackbar(t('leadCapture.webhooks.deletedSnackbar'));
            navigate('/settings/developers', { replace: true });
          } catch (requestError) {
            showErrorSnackbar(requestError);
            setIsDeleting(false);
          }
        }} />` : null}
    </div>
  <//>`;
}

